import { useEffect, useState } from "react";

import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Badge, Box, Divider, Paper, Stack, Tab, Tabs } from "@mui/material";

import { SUPPLIER_MULTI_SELECT_ACTIONS } from "@/features/chart-of-account-preferences";
import {
  EntityTaxInformationSection,
  mapEntityTaxInformationToPayload,
} from "@/features/entity-tax-information";
import { CenteredLoading, useSnackbar } from "../../ui";
import { AccountingSection } from "./accounting-section";
import { SupplierContactSection } from "./contact-section";
import { GeneralInformationSection } from "./general-information-section";
import { ProcurementSection } from "./procurement-section";
import { DEFAULT_VALUES, VALIDATION_SCHEMA } from "./supplier-form-constants";

// Each tab lists the form fields it owns so an invalid submit can (a) color the
// offending tab red and (b) jump the user straight to it — validation runs over
// the whole values object even while a tab is unmounted.
const TABS = [
  {
    label: "Informasi Umum",
    fields: [
      "name",
      "code",
      "supplierCategory",
      "type",
      "telephoneNumber",
      "phoneNumber",
      "whatsappNumber",
      "email",
      "fax",
      "website",
      "address",
      "postalCode",
      "city",
      "province",
      "country",
    ],
    render: () => <GeneralInformationSection />,
  },
  {
    label: "Kontak",
    fields: ["supplierContacts"],
    render: () => <SupplierContactSection />,
  },
  {
    label: "Pembelian",
    fields: ["defaultPaymentTerm", "sellerBankAccounts"],
    render: () => <ProcurementSection />,
  },
  {
    label: "Akun Pembelian",
    fields: ["accountingActionChartOfAccounts"],
    render: () => <AccountingSection />,
  },
  {
    label: "Pajak",
    fields: [
      "taxIdentificationNumber",
      "taxpayerName",
      "identityNumber",
      "defaultTaxIncluded",
      "taxInformationAddress",
      "taxInformationPostalCode",
      "taxInformationCity",
      "taxInformationProvince",
      "taxInformationCountry",
    ],
    render: () => <EntityTaxInformationSection />,
  },
];

export const SupplierForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  isLoading = false,
}) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const {
    reset,
    handleSubmit,
    formState,
    formState: { errors },
  } = methods;
  const { showSnackbar } = useSnackbar();

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const onFormSubmit = async (formData) => {
    const accountingActionChartOfAccounts = Object.entries(
      formData.accountingActionChartOfAccounts || {},
    ).flatMap(([action, value]) => {
      if (SUPPLIER_MULTI_SELECT_ACTIONS.has(action)) {
        return (value || []).map((coa) => ({
          action,
          chartOfAccountId: coa.id,
        }));
      }
      return value ? [{ action, chartOfAccountId: value.id }] : [];
    });

    return await onSubmit({
      name: formData.name,
      code: formData.code,
      telephoneNumber: formData.telephoneNumber || null,
      phoneNumber: formData.phoneNumber || null,
      whatsappNumber: formData.whatsappNumber || null,
      email: formData.email || null,
      fax: formData.fax || null,
      website: formData.website || null,
      country: formData.country || null,
      province: formData.province || null,
      city: formData.city || null,
      address: formData.address || null,
      postalCode: formData.postalCode || null,
      type: formData.type,
      defaultPaymentTermId: formData.defaultPaymentTerm.id,
      supplierCategoryId: formData.supplierCategory.id,
      supplierContacts: formData.supplierContacts.map((supplierContact) => ({
        name: supplierContact.name,
        position: supplierContact.position,
        email: supplierContact.email || null,
        phoneNumber: supplierContact.phoneNumber,
      })),
      sellerBankAccounts: formData.sellerBankAccounts.map(
        (sellerBankAccount) => ({
          accountHolder: sellerBankAccount.accountHolder,
          accountNumber: sellerBankAccount.accountNumber,
          bankId: sellerBankAccount.bank.id,
        })
      ),
      entityTaxInformation: mapEntityTaxInformationToPayload(formData),
      accountingActionChartOfAccounts,
    });
  };

  // A required field on a hidden tab can fail validation the user can't see —
  // jump to the first tab that has an error.
  const onInvalid = (formErrors) => {
    const erroredTab = TABS.findIndex(({ fields }) =>
      fields.some((field) => formErrors[field]),
    );
    if (erroredTab !== -1) {
      setTabIndex(erroredTab);
    }
    showSnackbar("Periksa kembali isian yang belum valid", "error");
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit, onInvalid)}
        spacing={3}
      >
        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, newValue) => setTabIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1 }}
          >
            {TABS.map(({ label, fields }) => {
              const hasError = fields.some((field) => errors[field]);
              return (
                <Tab
                  key={label}
                  label={
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={!hasError}
                      sx={{ "& .MuiBadge-badge": { right: -8, top: 2 } }}
                    >
                      {label}
                    </Badge>
                  }
                  sx={{ color: hasError ? "error.main" : undefined }}
                />
              );
            })}
          </Tabs>
          <Divider />

          <Box sx={{ p: 3 }}>
            {TABS.map((tab, index) => (
              <Box
                key={tab.label}
                sx={{ display: tabIndex === index ? "block" : "none" }}
              >
                {tab.render()}
              </Box>
            ))}
          </Box>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Box>
      </Stack>
    </FormProvider>
  );
};

SupplierForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  isLoading: PropTypes.bool,
};
